import { useEffect, useRef } from 'react'
import type { Product } from '@/types'

/**
 * Local persistence for the in-progress NEW-order draft a cashier is building,
 * so a reload / crash / network blip doesn't vaporize a staged order.
 *
 * Stored in localStorage (the cart is small — we persist productId + qty + note,
 * never the Product object, which carries a multi-MB base64 image and would
 * freeze prices). Namespaced by the logged-in user so two cashiers sharing a
 * terminal never inherit each other's draft. This does NOT enable offline
 * selling — it only prevents data loss and powers the reconnect prompt.
 */

const DRAFT_KEY_PREFIX = 'pos-counter-draft-v1'
/** A held order older than one shift is almost always noise (and stale prices). */
export const DRAFT_MAX_AGE_MS = 12 * 60 * 60 * 1000

export interface PersistedDraftLine {
  productId: string
  quantity: number
  special_instructions?: string
}

export interface PersistedDraft {
  version: 1
  userId: string
  savedAt: number
  orderType: string
  cart: PersistedDraftLine[]
  orderNotes: string
  customerName: string
  customerEmail: string
  customerPhone: string
  guestBirthday: string
  deliveryAddress: string
  deliveryFeeOverride: number | null
}

/** Fields that make up a draft, minus the bookkeeping (`version`/`userId`/`savedAt`). */
export type DraftFields = Omit<PersistedDraft, 'version' | 'userId' | 'savedAt'>

function draftKey(userId: string): string {
  return `${DRAFT_KEY_PREFIX}:${userId}`
}

/** The logged-in user's id from `pos_user`, or null if unreadable. Never write an un-namespaced key. */
export function getDraftUserId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem('pos_user')
    if (!raw) return null
    const u = JSON.parse(raw) as { id?: string }
    return typeof u.id === 'string' && u.id ? u.id : null
  } catch {
    return null
  }
}

/** A draft with no line items isn't worth persisting. */
function isEmptyDraft(fields: DraftFields): boolean {
  return fields.cart.length === 0
}

export function readDraft(userId: string): PersistedDraft | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(draftKey(userId))
    if (!raw) return null
    const d = JSON.parse(raw) as PersistedDraft
    if (d.version !== 1 || d.userId !== userId || !Array.isArray(d.cart)) return null
    if (typeof d.savedAt !== 'number' || Date.now() - d.savedAt > DRAFT_MAX_AGE_MS) return null
    return d
  } catch {
    return null
  }
}

export function clearDraft(userId: string | null): void {
  if (typeof window === 'undefined' || !userId) return
  try {
    localStorage.removeItem(draftKey(userId))
  } catch {
    /* ignore */
  }
}

function writeDraft(userId: string, fields: DraftFields): void {
  if (typeof window === 'undefined') return
  try {
    if (isEmptyDraft(fields)) {
      localStorage.removeItem(draftKey(userId))
      return
    }
    const payload: PersistedDraft = {
      version: 1,
      userId,
      savedAt: Date.now(),
      ...fields,
    }
    localStorage.setItem(draftKey(userId), JSON.stringify(payload))
  } catch {
    /* localStorage full/blocked — non-fatal; the order still lives in React state */
  }
}

/**
 * Debounced autosave of the composed draft. `enabled` should be false whenever
 * the order is no longer a fresh draft (i.e. it became a server-side order, so
 * `continuingOrderId != null`) or there's no user to key on. Also flushes on
 * tab-hide / unload so a crash between debounce ticks still captures the latest.
 *
 * `fields` should be a memoized object (keyed on its constituent state) so the
 * debounce effect only re-arms when the draft actually changes.
 */
export function useDraftAutosave(
  userId: string | null,
  fields: DraftFields,
  enabled: boolean,
): void {
  const fieldsRef = useRef(fields)
  fieldsRef.current = fields

  // Debounced write on change.
  useEffect(() => {
    if (!enabled || !userId) return
    const t = window.setTimeout(() => writeDraft(userId, fieldsRef.current), 500)
    return () => window.clearTimeout(t)
  }, [enabled, userId, fields])

  // Belt-and-suspenders: flush immediately on hide / unload.
  useEffect(() => {
    if (!enabled || !userId) return
    const flush = () => writeDraft(userId, fieldsRef.current)
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('beforeunload', flush)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('beforeunload', flush)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enabled, userId])
}

export interface RestoredCartLine {
  product: Product
  quantity: number
  special_instructions?: string
}

/**
 * Reconcile persisted lines against the live menu. Unknown/unavailable products
 * are dropped; surviving lines are rebuilt from the FRESH product object, so
 * prices are always current (no stale prices are ever resurrected).
 */
export function restoreDraftItems(
  lines: PersistedDraftLine[],
  liveProducts: Product[],
): { items: RestoredCartLine[]; dropped: number } {
  const byId = new Map(liveProducts.map((p) => [p.id, p]))
  const items: RestoredCartLine[] = []
  let dropped = 0
  for (const line of lines) {
    const product = byId.get(line.productId)
    if (!product || product.is_available === false) {
      dropped++
      continue
    }
    items.push({
      product,
      quantity: line.quantity,
      special_instructions: line.special_instructions,
    })
  }
  return { items, dropped }
}
